import React, { useState } from "react";
import {
    Modal,
    Form,
    Input,
    Button,
    notification,
    Select,
    Tag,
    Tabs,
    Slider,
    DatePicker,
    Popconfirm,
    Spin,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import {
    getResource,
    updateResource,
    deleteResource,
    getServicesCategories,
    createAvailabilityException,
    deleteAvailabilityException,
} from "../../../service";
import {
    convertTimeToNumber,
    convertToTime,
    maxPoints,
    weekDayLabels,
} from "../../../utils";
import { colors } from "../../../styles/theme";

const { RangePicker } = DatePicker;

const defaultAvailabilitySchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
};

interface Service {
    id: number;
    name: string;
    serviceCategoryId: number;
}

interface ServiceCategory {
    id: number;
    name: string;
    services?: Service[];
}

interface ResourceAvailability {
    id: number;
    config: any;
    companyBranchId: number;
    resourceId: number;
}

interface AvailabilityException {
    availabilityExceptionId: number;
    resourceId: number;
    availabilityException: {
        id: number;
        from: string;
        till: string;
    };
}

interface Resource {
    id: number;
    name: string;
    resourceCategoryId: number;
    providesServices?: { serviceId: number; resourceId: number; service: Service }[];
    availability?: ResourceAvailability;
    availabilityExceptions?: AvailabilityException[];
}

interface EditResourceModalProps {
    open: boolean;
    onCancel: () => void;
    resourceId: number | null;
    categoryId: number;
    onSuccess: () => void;
}

export const EditResourceModal: React.FC<EditResourceModalProps> = ({
    open,
    onCancel,
    resourceId,
    categoryId,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [selectedServices, setSelectedServices] = useState<number[]>([]);
    const [availabilitySchedule, setAvailabilitySchedule] = useState<any>(defaultAvailabilitySchedule);
    const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
    const [exceptionDates, setExceptionDates] = useState<any>(null);

    const { data: resource, isLoading } = useQuery<Resource>(
        ["/resource", resourceId],
        () => getResource({ id: resourceId! }),
        {
            enabled: !!resourceId && open,
            onSuccess(data) {
                form.setFieldsValue({
                    name: data.name,
                });

                if (data.providesServices) {
                    setSelectedServices(
                        data.providesServices.map((s) => s.serviceId)
                    );
                }

                if (data.availability?.config) {
                    const mergedSchedule = {
                        ...defaultAvailabilitySchedule,
                        ...data.availability.config,
                    };
                    setAvailabilitySchedule(mergedSchedule);
                } else {
                    setAvailabilitySchedule(defaultAvailabilitySchedule);
                }
            },
        }
    );

    const { data: serviceCategories } = useQuery<ServiceCategory[]>(
        ["/services-categories"],
        () => getServicesCategories(),
        {
            enabled: open,
        }
    );

    const { mutateAsync: updateMutation, isLoading: updateIsLoading } =
        useMutation(updateResource, {
            onSuccess: () => {
                notification.success({
                    message: t("editResourceModal.resourceUpdated"),
                    style: { background: colors.successBackground },
                });
                onSuccess();
            },
            onError: () => {
                notification.error({
                    message: t("editResourceModal.resourceUpdateError"),
                    style: { background: colors.errorBackground },
                });
            },
        });

    const { mutateAsync: deleteMutation, isLoading: deleteIsLoading } =
        useMutation(deleteResource, {
            onSuccess: () => {
                notification.success({
                    message: t("editResourceModal.resourceDeleted"),
                    style: { background: colors.successBackground },
                });
                onSuccess();
            },
            onError: () => {
                notification.error({
                    message: t("editResourceModal.resourceDeleteError"),
                    style: { background: colors.errorBackground },
                });
            },
        });

    const { mutateAsync: createExceptionMutation } = useMutation(
        createAvailabilityException,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/resource", resourceId]);
                notification.success({
                    message: t("editResourceModal.exceptionAdded"),
                    style: { background: colors.successBackground },
                });
            },
            onError: () => {
                notification.error({
                    message: t("editResourceModal.exceptionAddError"),
                    style: { background: colors.errorBackground },
                });
            },
        }
    );

    const { mutateAsync: deleteExceptionMutation } = useMutation(
        deleteAvailabilityException,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["/resource", resourceId]);
                notification.success({
                    message: t("editResourceModal.exceptionDeleted"),
                    style: { background: colors.successBackground },
                });
            },
            onError: () => {
                notification.error({
                    message: t("editResourceModal.exceptionDeleteError"),
                    style: { background: colors.errorBackground },
                });
            },
        }
    );

    const addRange = (day: string) => {
        setAvailabilitySchedule((current: any) => {
            return {
                ...current,
                [day]: [...current[day], { from: "8:00", till: "16:00" }],
            };
        });
    };

    const editRange = (day: string, index: number, value: any) => {
        setAvailabilitySchedule((current: any) => {
            const newArray = current[day];
            newArray[index] = value;
            return {
                ...current,
                [day]: [...newArray],
            };
        });
    };

    const removeRange = (day: string) => {
        setAvailabilitySchedule((current: any) => {
            const newArray = current[day];
            newArray.pop();
            return {
                ...current,
                [day]: newArray,
            };
        });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (resourceId && resource) {
                await updateMutation({
                    id: resource.id,
                    body: {
                        ...values,
                        resourceCategoryId: resource.resourceCategoryId,
                        providesServices: selectedServices,
                        availabilitySchedule: availabilitySchedule,
                    },
                });
            }
        } catch (error) {
            // Validation fail
        }
    };

    const handleDelete = async () => {
        if (resource) {
            await deleteMutation(resource.id);
        }
    };

    const handleRemoveService = (serviceId: number) => {
        setSelectedServices((prev) => prev.filter((id) => id !== serviceId));
    };

    // Get all services from all categories
    const allServices = serviceCategories?.flatMap((cat) => cat.services || []) || [];

    // Get available services (not already selected)
    const availableServices = allServices.filter((s) => !selectedServices.includes(s.id));

    if (!open) return null;

    return (
        <Modal
            title={t("editResourceModal.title")}
            open={open}
            onCancel={onCancel}
            width={1000}
            footer={[
                <Button key="delete" danger loading={deleteIsLoading} onClick={handleDelete}>
                    {t("editResourceModal.deleteResource")}
                </Button>,
                <Button key="cancel" onClick={onCancel}>
                    {t("editResourceModal.cancel")}
                </Button>,
                <Button key="save" type="primary" loading={updateIsLoading} onClick={handleSave}>
                    {t("editResourceModal.saveChanges")}
                </Button>,
            ]}
        >
            {isLoading ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Tabs defaultActiveKey="basic" items={[
                    {
                        key: "basic",
                        label: t("editResourceModal.tabBasic"),
                        children: (
                            <Form form={form} layout="vertical" style={{ padding: "16px 0" }}>
                                <Form.Item
                                    label={t("editResourceModal.nameLabel")}
                                    name="name"
                                    rules={[{ required: true, message: t("editResourceModal.nameRequired") }]}
                                >
                                    <Input placeholder={t("editResourceModal.namePlaceholder")} />
                                </Form.Item>
                            </Form>
                        )
                    },
                    {
                        key: "services",
                        label: t("editResourceModal.tabServices"),
                        children: (
                            <div style={{ padding: "16px 0" }}>
                                <p style={{ marginBottom: 16, color: colors.textSecondary, fontSize: 13 }}>
                                    {t("editResourceModal.servicesDescription")}
                                </p>
                                <div style={{ marginBottom: 16 }}>
                                    {selectedServices.map((serviceId) => {
                                        const service = allServices.find((s) => s.id === serviceId);
                                        return service ? (
                                            <Tag
                                                key={serviceId}
                                                closable
                                                onClose={() => handleRemoveService(serviceId)}
                                                color="blue"
                                                style={{ marginBottom: 8 }}
                                            >
                                                {service.name}
                                            </Tag>
                                        ) : null;
                                    })}
                                </div>
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder={t("editResourceModal.addServicePlaceholder")}
                                    value={null}
                                    onChange={(value) => {
                                        if (value) setSelectedServices((prev) => [...prev, value]);
                                    }}
                                    options={availableServices.map((s) => ({
                                        label: s.name,
                                        value: s.id,
                                    }))}
                                />
                            </div>
                        )
                    },
                    {
                        key: "schedule",
                        label: t("editResourceModal.tabSchedule"),
                        children: (
                            <div style={{ padding: "16px 0" }}>
                                <Tabs
                                    defaultActiveKey="monday"
                                    tabPosition="left"
                                    items={Object.entries(availabilitySchedule).map((entry: any) => {
                                        return {
                                            label: weekDayLabels[entry[0]] || entry[0],
                                            key: entry[0],
                                            children: (
                                                <div style={{ paddingLeft: "20px" }}>
                                                    {entry[1].length === 0 ? (
                                                        <div style={{ textAlign: "center", padding: "20px 0", color: colors.textSecondary }}>
                                                            <p>{t("editResourceModal.noTimeRanges")}</p>
                                                        </div>
                                                    ) : (
                                                        entry[1].map((e: any, i: number) => {
                                                            return (
                                                                <Slider
                                                                    key={i}
                                                                    range
                                                                    min={0}
                                                                    max={maxPoints}
                                                                    step={1}
                                                                    value={[
                                                                        convertTimeToNumber(e.from),
                                                                        convertTimeToNumber(e.till),
                                                                    ]}
                                                                    onChange={(newRange) =>
                                                                        editRange(entry[0], i, {
                                                                            from: convertToTime(newRange[0]),
                                                                            till: convertToTime(newRange[1]),
                                                                        })
                                                                    }
                                                                    marks={{
                                                                        0: "00:00",
                                                                        32: "08:00",
                                                                        64: "16:00",
                                                                        96: "23:45",
                                                                    }}
                                                                    tooltip={{
                                                                        formatter(value) {
                                                                            if (value !== undefined)
                                                                                return convertToTime(value);
                                                                        },
                                                                    }}
                                                                />
                                                            );
                                                        })
                                                    )}

                                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                                                        <Button
                                                            danger
                                                            size="small"
                                                            onClick={() => removeRange(entry[0])}
                                                            disabled={entry[1].length === 0}
                                                        >
                                                            {t("editResourceModal.removeLast")}
                                                        </Button>
                                                        <Button onClick={() => addRange(entry[0])} size="small">
                                                            {t("editResourceModal.addRange")}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ),
                                        };
                                    })}
                                />
                            </div>
                        )
                    },
                    {
                        key: "exceptions",
                        label: t("editResourceModal.tabExceptions"),
                        children: (
                            <div style={{ padding: "16px 0" }}>
                                <p style={{ marginBottom: 16, color: colors.textSecondary, fontSize: 13 }}>
                                    {t("editResourceModal.exceptionsDescription")}
                                </p>
                                {resource?.availabilityExceptions && resource.availabilityExceptions.length > 0 ? (
                                    <div style={{ marginBottom: 16 }}>
                                        {resource.availabilityExceptions.map((exception, index) => (
                                            <div
                                                key={index}
                                                style={{
                                                    background: "var(--color-error-bg)",
                                                    padding: 12,
                                                    borderRadius: 8,
                                                    marginBottom: 8,
                                                    border: "1px solid var(--color-error)",
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div>
                                                    <div style={{ fontSize: 14 }}>
                                                        <strong>{t("editResourceModal.from")}</strong> {new Date(exception.availabilityException.from).toLocaleString("pl-PL")}
                                                    </div>
                                                    <div style={{ fontSize: 14 }}>
                                                        <strong>{t("editResourceModal.to")}</strong> {new Date(exception.availabilityException.till).toLocaleString("pl-PL")}
                                                    </div>
                                                </div>
                                                <Popconfirm
                                                    title={t("editResourceModal.deleteExceptionConfirm")}
                                                    onConfirm={() => deleteExceptionMutation(exception.availabilityException.id)}
                                                    okText={t("editResourceModal.yes")}
                                                    cancelText={t("editResourceModal.no")}
                                                >
                                                    <Button danger size="small">
                                                        {t("editResourceModal.delete")}
                                                    </Button>
                                                </Popconfirm>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ color: colors.textSecondary, marginBottom: 16 }}>{t("editResourceModal.noExceptions")}</p>
                                )}
                                <Button
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsExceptionModalOpen(true)}
                                >
                                    {t("editResourceModal.addException")}
                                </Button>

                                <Modal
                                    title={t("editResourceModal.addExceptionModalTitle")}
                                    open={isExceptionModalOpen}
                                    onOk={async () => {
                                        if (exceptionDates && exceptionDates.length === 2 && resourceId) {
                                            try {
                                                await createExceptionMutation({
                                                    from: exceptionDates[0].toISOString(),
                                                    till: exceptionDates[1].toISOString(),
                                                    resourceIds: [resourceId],
                                                });
                                                setIsExceptionModalOpen(false);
                                                setExceptionDates(null);
                                            } catch (error) {
                                                // Error is handled by mutation's onError
                                            }
                                        } else {
                                            notification.error({
                                                message: t("editResourceModal.selectDateRange"),
                                            });
                                        }
                                    }}
                                    onCancel={() => {
                                        setIsExceptionModalOpen(false);
                                        setExceptionDates(null);
                                    }}
                                >
                                    <p style={{ marginBottom: 16 }}>{t("editResourceModal.selectUnavailabilityPeriod")}</p>
                                    <RangePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm"
                                        style={{ width: "100%" }}
                                        onChange={(dates) => setExceptionDates(dates)}
                                    />
                                </Modal>
                            </div>
                        )
                    }
                ]} />
            )}
        </Modal>
    );
};
